import { TAnalyticsReportsFilter } from "@/interfaces/api";
import {
  SHARE_OPEN_LINK_ACTION,
  SHARE_COPY_LINK_ACTION,
  CLIENTS_PERMISSIONS as CLIENTS,
} from "@lib/constants/clients";
import { PORTFOLIO_BUILDER_PERMISSIONS as PORTFOLIO_BUILDER } from "@lib/constants/portfolio-builder";
import { ORG_ADMIN_ROLE, ORG_ADVISOR_ROLE, SUPER_ADMIN_ROLE, CLIENT_PERMISSIONS_TO_ROLES } from "@lib/constants/advisor";
export { ORG_ADMIN_ROLE, ORG_ADVISOR_ROLE, SUPER_ADMIN_ROLE, ORG_CLIENT_ROLE, USER_ROLES } from "@lib/constants/advisor";

export {
  CURRENCY_BRL,
  CURRENCY_EUR,
  CURRENCY_USD,
  CURRENCY_SEK,
  CURRENCY_GBP,
  CURRENCY_GBX,
  BASE_CURRENCY,
  BASE_CURRENCY_MAP,
  type TBaseCurrencyMapKey,
} from "@lib/constants/currency";

export const PERMISSIONS = {
  CLIENTS,
  USERS: {
    MANAGE: "PERMISSIONS.USERS.MANAGE",
    VIEW: "PERMISSIONS.USERS.VIEW",
  },
  SETTINGS_NJORDA: {
    VIEW: "PERMISSIONS.SETTINGS_NJORDA.VIEW",
  },
  APP_SETTINGS: {
    VIEW: "PERMISSIONS.APP_SETTINGS.VIEW",
  },
  SETTINGS: {
    VIEW: "PERMISSIONS.SETTINGS.VIEW",
  },
  INTEGRATIONS: {
    VIEW: "PERMISSIONS.INTEGRATIONS.VIEW",
  },
  ASSET_LIST_RULES: {
    VIEW: "PERMISSIONS.ASSET_LIST_RULES.VIEW",
  },
  CALCULATION_PARAMETERS: {
    VIEW: "PERMISSIONS.CALCULATION_PARAMETERS.VIEW",
  },
  ASSETS: {
    VIEW: "PERMISSIONS.ASSETS.VIEW",
  },
  ASSETS_CLASSES: {
    VIEW: "PERMISSIONS.ASSETS_CLASSES.VIEW",
  },
  ASSETS_ALLOCATIONS: {
    VIEW: "PERMISSIONS.ASSETS_ALLOCATIONS.VIEW",
  },
  BENCHMARKS: {
    VIEW: "PERMISSIONS.BENCHMARKS.VIEW",
  },
  PORTFOLIO_TEMPLATES: {
    VIEW: "PERMISSIONS.PORTFOLIO_TEMPLATES.VIEW",
    MANAGE: "PERMISSIONS.PORTFOLIO_TEMPLATES.MANAGE",
  },
  RISK_SCENARIOS: {
    VIEW: "PERMISSIONS.RISK_SCENARIOS.VIEW",
    MANAGE: "PERMISSIONS.RISK_SCENARIOS.MANAGE",
  },
  ANALYTICS_REPORTS: {
    VIEW: "PERMISSIONS.ANALYTICS_REPORTS.VIEW",
  },
  PORTFOLIO_BUILDER,
};

export const USER_PERMISSIONS = {
  ...CLIENT_PERMISSIONS_TO_ROLES,
  [PERMISSIONS.USERS.MANAGE]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.USERS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.SETTINGS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.INTEGRATIONS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.CALCULATION_PARAMETERS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.ASSET_LIST_RULES.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.ASSETS_CLASSES.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.SETTINGS_NJORDA.VIEW]: [SUPER_ADMIN_ROLE],
  [PERMISSIONS.APP_SETTINGS.VIEW]: [SUPER_ADMIN_ROLE],
  [PERMISSIONS.ASSETS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.ASSETS_ALLOCATIONS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.BENCHMARKS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.PORTFOLIO_TEMPLATES.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE, ORG_ADVISOR_ROLE],
  [PERMISSIONS.PORTFOLIO_TEMPLATES.MANAGE]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.RISK_SCENARIOS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.RISK_SCENARIOS.MANAGE]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.ANALYTICS_REPORTS.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE],
  [PERMISSIONS.PORTFOLIO_BUILDER.VIEW]: [SUPER_ADMIN_ROLE, ORG_ADMIN_ROLE, ORG_ADVISOR_ROLE],
};

export enum EXPORT_FORMATS {
  CSV = "csv",
  XLSX = "xlsx",
}

export const COMMON_LIST_ACTIONS = {
  VIEW: "view",
  DELETE: "delete",
};

export const ADVISOR_ACTIONS = {
  ...COMMON_LIST_ACTIONS,
  EXPORT: {
    CSV: "export.csv",
    XLSX: "export.xlsx",
  },
};

export const ANALYTICS_REPORT_REQUEST_TIMEOUT = 1500;

export const ANALYTICS_REPORT_CALCULATE_STATUSES = {
  DONE: "Unsaved",
  PENDING: "Processing",
  ERROR: "Error",
};

export const ANALYTICS_REPORTS_COLUMNS_TYPES = {
  STRING: "String",
  PRICE: "Price",
  PERCENTAGE: "Percentage",
};

export const ANALYTICS_REPORTS_FILTERS_DEFAULT_CHOICE = TAnalyticsReportsFilter.id.EQ;

export const ANALYTICS_REPORTS_FILTERS_CHOISES: {
  id: TAnalyticsReportsFilter.id;
  name: string;
  isValueNeeded?: boolean;
  isArrayValueNeeded?: boolean;
}[] = [
  {
    id: TAnalyticsReportsFilter.id.EQ,
    name: "equal",
    isValueNeeded: true,
  },
  {
    id: TAnalyticsReportsFilter.id.LT,
    name: "less-than",
    isValueNeeded: true,
  },
  {
    id: TAnalyticsReportsFilter.id.GT,
    name: "greater-than",
    isValueNeeded: true,
  },
  {
    id: TAnalyticsReportsFilter.id.CONTAIN,
    name: "contain",
    isValueNeeded: true,
  },
  {
    id: TAnalyticsReportsFilter.id.EMPTY,
    name: "is-empty",
  },
  {
    id: TAnalyticsReportsFilter.id.NOT_EMPTY,
    name: "is-not-empty",
  },
  {
    id: TAnalyticsReportsFilter.id.ERROR,
    name: "is-error",
  },
  {
    id: TAnalyticsReportsFilter.id.NOT_ERROR,
    name: "is-not-error",
  },
  {
    id: TAnalyticsReportsFilter.id.IN,
    name: "in",
    isArrayValueNeeded: true,
  },
  {
    id: TAnalyticsReportsFilter.id.NOT_IN,
    name: "not_in",
    isArrayValueNeeded: true,
  },
];

export const ANALYTICS_REPORTS_GROUPINGS = {
  USER: "user",
};

export enum ANALYTICS_REPORTS_CHART_TYPES {
  BAR = "bar",
  SCATTER = "scatter",
};

export const SHARE_LINK_ACTIONS = [SHARE_COPY_LINK_ACTION, SHARE_OPEN_LINK_ACTION];

export const PORTFOLIO_BUILDER_MODE_FULL = "normal";
export const PORTFOLIO_BUILDER_MODE_SIMPLE = "simplified";

export type TAssetListType = "portfolio_builder" | "asset_class";

export const ASSET_LIST_TYPES: Record<string, TAssetListType> = {
  PORTFOLIO_BUILDER: "portfolio_builder",
  ASSET_CLASS: "asset_class",
};

export const REPORT_ENABLE_MANUAL_ACCOUNTS = [
  { id: "enable_manual_accounts", value: false },
  { id: "disable_manual_accounts", value: true },
];

export const REPORT_VIEW = ["simple", "allocations_and_positions", "detailed_view"];

export const COLLECT_IDENTITY = true;
export const FORM_USER_ROLES = [ORG_ADMIN_ROLE, ORG_ADVISOR_ROLE];

export const MAPPING_TYPE_PRIORITY_RANK = {
  ticker: 4,
  asset_list: 3,
  instrument_type: 2,
  default: 1,
};

export const PORTFOLIO_PROPOSAL_MODE = {
  ASSETS_MODE: "ASSETS_MODE",
  ALLOCATION_MODE: "ALLOCATION_MODE",
};
export const UNKNOWN_ASSETS_LIST_ID = "UNKNOWN_ASSETS";
export const PORTFOLIO_PROPOSALS_ASSET_LIST_TYPES_WHITELIST: TAssetListType[] = [ASSET_LIST_TYPES.PORTFOLIO_BUILDER as TAssetListType];

export const PORTFOLIO_TEMPLATE_SCOPES = {
  USER: "user",
  ORGANIZATION: "organization_app",
};

export const PORTFOLIO_PROPOSALS_ALLOCATION_ACTIONS = {
  EQUAL_WEIGHT: "equal_weight",
  FIX_TO_100_PERCENT: "fix_to_100",
  CLEAR_ALL: "clear_all",
};

export const PORTFOLIO_PROPOSAL_ACTIONS = {
  ...PORTFOLIO_PROPOSALS_ALLOCATION_ACTIONS,
  COPY_FROM_OTHER: "copy_from_other",
  SHOW_QUANTITIES: "show_quantities",
  SHOW_RISKS: "show_risks",
  OPTIMIZE: "optimize_proposal",
  DUPLICATE: "duplicate",
  DUPLICATE_AND_OPTIMIZE: "duplicate_and_optimize_proposal",
  OPTIMIZE_RETURNS: "optimize_returns",
};

export const RISK_PERIOD_OPTIONS = [1, 2, 3, 4, 5];

export const DEFAULT_PORTFOLIO_PROPOSAL_VALUE = 0;
export const DEFAULT_PORTFOLIO_PROPOSAL_INVESTMENT_PERIOD = 1;
export const DEFAULT_PORTFOLIO_PROPOSAL_RISK_PERIOD = RISK_PERIOD_OPTIONS[0];

export const PORTFOLIO_PROPOSALS_ASSET_PRICE_HISTORY_RANGE = 730;

export const PORTFOLIO_PROPOSALS_ASSET_CHART_RANGES = {
  "1m": 30,
  "3m": 90,
  "1y": 365,
  "all-time": 730,
};

export const PORTFOLIO_PROPOSALS_ASSET_CHART_RANGE_BUTTONS = Object.keys(PORTFOLIO_PROPOSALS_ASSET_CHART_RANGES).map(
  (key) => ({
    slug: key,
    label: `advisor.portfolioProposals.proposalForm.assetsPicker.chartRange.${key}`,
  }),
);

export const PORTFOLIO_BUILDER_EXPORT_FORMATS = {
  PDF: "pdf",
  XLSX: "xlsx",
};

export const PORTFOLIO_BUILDER_CALCULATIONS_RISKS_WARNING_TYPE = {
  SVP_SP_ER_PARAM_USED: "SVP_SP_ER_PARAM_USED",
};
